<template>
  <div class="match-columns">
    <div>
      <p class="font-weight-semi-bold font-xl mb-0">{{ $t('audience-new.match-columns.step-intro') }}</p>
      <p>{{ $t('audience-new.match-columns.step-description') }}</p>
    </div>
    <div class="mb-5">
      <SenditTable class="max-h-500 overflow-auto" :headers="tableHeaders" :registers="tableRegisters">
        <template #fileColumnName="props">
          <td class="vertical-middle text-uppercase">{{ props.item.fileColumnName }}</td>
        </template>
        <template #lines="props">
          <td class="vertical-middle max-w-450 hidden-text">{{ props.item.lines }}</td>
        </template>
        <template #matchColumn="props">
          <td class="w-450 max-w-450">
            <div v-if="isSelectedRowDateOrNew(props.item)">
              <div v-if="isDate">
                <CSelect
                  :options="formatDateOptions"
                  :disabled="hasPredefinedDateFormatForced(props.item)"
                  add-input-classes="border-input"
                  :placeholder="$t('audience-new.match-columns.select-date.placeholder')"
                  :value.sync="dateFormat"
                  :class="{ 'bg-gray-200': hasPredefinedDateFormatForced(props.item) }"
                  class="mb-0"
                />
              </div>
              <div v-if="isNewType">
                <CInput
                  v-model="newType.value"
                  addInputClasses="text-uppercase placeholder-transform-none border-input"
                  class="mb-0 mt-2"
                  :placeholder="$t('audience-new.match-columns.new-type.input.placeholder')"
                />
              </div>
              <div class="d-flex w-100 mt-2">
                <CButton
                  v-if="isNewType"
                  :disabled="!newType.value.length && (!isDate || isNewType || dateFormat !== null)"
                  @click.stop="setNewType(props.item)"
                  class="btn-primary font-weight-semi-bold text-white"
                >
                  {{ $t('audience-new.match-columns.new-type.button.confirm') }}
                </CButton>
                <CButton
                  v-if="!isNewType && isDate"
                  :disabled="!dateFormat"
                  @click.stop="confirmDateFormat(props.item)"
                  class="btn-primary font-weight-semi-bold text-white"
                >
                  {{ $t('audience-new.match-columns.new-type.button.confirm') }}
                </CButton>
                <CButton @click.stop="clear(props.item, true)" class="btn-outline-gray-500 font-weight-semi-bold ml-2">
                  {{ $t('audience-new.match-columns.new-type.button.cancel') }}
                </CButton>
              </div>
            </div>
            <div v-else>
              <multiselect
                class="mb-2"
                :multiple="false"
                :disabled="isLoadingAnySelectType || selectTypesOptions.length === 0 || props.item.state.key === 3"
                :loading="isLoadingAnySelectType"
                :options="selectTypesOptions"
                :group-label="'value' || 'group'"
                :maxHeight="200"
                :group-select="false"
                :showLabels="false"
                :placeholder="null"
                :value="props.item.type"
                openDirection="bottom"
                group-values="types"
                label="value"
                track-by="value"
                id="selectType"
                @select="selectDataType($event, props.item)"
              >
                <template slot="singleLabel">
                  <span>{{ getSingleLabelValue(props.item.type) }}</span>
                </template>
                <template slot="option" slot-scope="props" :data-cy="setAttr(props)">
                  <div class="option__desc" :data-cy="setAttr(props)">
                    <template v-if="props.option.$isLabel">
                      <span class="multiselect__optionLabelGroup">{{ props.option.$groupLabel | removeCaracterSpecial }}</span>
                    </template>
                    <template>
                      <span class="option__title">{{ props.option.value }} </span>
                      <span v-if="props.option.dataType" class="option__title">({{ props.option.dataType.value }})</span>
                    </template>
                  </div>
                </template>
              </multiselect>
              <span v-if="!props.item.type && props.item.state.key !== 3" class="text-warning font-sm">{{ $t('audience-new.match-columns.please-match') }}</span>
            </div>
          </td>
        </template>
        <template #ignoreColumn="props">
          <td class="vertical-middle text-center checkbox-area max-w-150" :class="{ 'row-ignored': props.item.state.key === 3 }">
            <CInputCheckbox custom @update:checked="ignoreColumn($event, props.item)" :disabled="props.item.isKey" />
          </td>
        </template>
        <template #isKey="props">
          <td class="vertical-middle text-center max-w-150">
            <div>
              <CIcon v-if="props.item.isKey" name="key" size="custom-size" :height="22" class="text-primary" viewBox="0 0 32 32" />
            </div>
          </td>
        </template>
      </SenditTable>
    </div>
    <div class="mb-5">
      <CAlert v-if="showAlertKeyRequired" class="mb-3 font-sm max-w-890" color="warning">
        <div class="d-flex align-items-baseline text-black">
          <div>
            <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="15" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
          </div>
          <p class="mb-0">
            {{ $t('audience-new.match-columns.key-column.description') }}
          </p>
        </div>
      </CAlert>
      <CButton
        color="primary"
        @click="openKeyColumnModal"
        :disabled="isMatchIncomplete || !hasAnySMSorEmailColumn || hasAnyAudienceKeyOnImport"
        variant="outline"
        class="px-4 py-1"
      >
        <div class="d-flex align-items-center">
          <span class="mr-2">
            {{ $t('audience-new.match-columns.key-column.set') }}
          </span>
          <CIcon name="key" size="custom-size" :height="16" viewBox="0 0 32 32" />
        </div>
      </CButton>
    </div>
    <transition name="fade">
      <KeyColumnModal ref="keyColumnModal" :showModal="showKeyColumnModal" />
    </transition>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import SenditTable from '@/components/table/SenditTable.vue'
import services from '@/services.js'
import KeyColumnModal from '../KeyColumnModal.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'MatchColumns',
  components: { SenditTable, vSelect, KeyColumnModal },
  data() {
    return {
      selectTypes: [],
      defaultSelectTypes: undefined,
      isLoadingSelectTypes: false,
      isLoadingDefaultSelectTypes: false,
      isDate: false,
      dateFormat: null,
      selectedDataTypeAux: null,
      selectedRowAux: null,
      newType: {
        group: '',
        value: '',
        hasName: false,
        format: undefined,
      },
      formatDateOptions: [
        { label: 'dd-mm-yyyy', value: 'dd-mm-yyyy' },
        { label: 'mm-dd-yyyy', value: 'mm-dd-yyyy' },
        { label: 'yyyy-dd-mm', value: 'yyyy-dd-mm' },
        { label: 'yyyy-mm-dd', value: 'yyyy-mm-dd' },
        { label: 'dd/mm/yyyy', value: 'dd/mm/yyyy' },
        { label: 'mm/dd/yyyy', value: 'mm/dd/yyyy' },
        { label: 'yyyy/dd/mm', value: 'yyyy/dd/mm' },
        { label: 'yyyy/mm/dd', value: 'yyyy/mm/dd' },
        { label: 'dd.mm.yyyy', value: 'dd.mm.yyyy' },
        { label: 'mm.dd.yyyy', value: 'mm.dd.yyyy' },
        { label: 'yyyy.dd.mm', value: 'yyyy.dd.mm' },
        { label: 'yyyy.mm.dd', value: 'yyyy.mm.dd' },
      ],
      showKeyColumnModal: false,
    }
  },
  mounted() {
    this.getDefaultFieldDataTypes()
    this.getSelectTypes()
  },
  computed: {
    ...mapGetters({
      hasAnyAudienceKeyOnImport: 'audience/hasAnyAudienceKeyOnImport',
      hasAnyAudienceKeyOnItem: 'audience/hasAnyAudienceKeyOnItem',
    }),
    organizationId() {
      return this.$route.params.organizationId
    },
    isNewType() {
      return this.newType.group === 'CUSTOM'
    },
    tableHeaders() {
      const headers = [
        {
          key: 'fileColumnName',
          label: this.$t('audience-new.match-columns.table.header.col-header'),
          sorter: false,
        },
        { key: 'lines', label: this.$t('audience-new.match-columns.table.header.audience-data'), sorter: false },
        { key: 'matchColumn', label: this.$t('audience-new.match-columns.table.header.match-columns'), sorter: false },
        { key: 'ignoreColumn', label: this.$t('audience-new.match-columns.table.header.ignore-column'), sorter: false, _classes: 'text-center' },
      ]

      if (this.hasAnyAudienceKeyOnImport || this.hasAnyAudienceKeyOnItem) {
        headers.push({ key: 'isKey', label: this.$t('audience-new.match-columns.table.header.key-column'), sorter: false, _classes: 'text-center' })
      }

      return headers
    },
    tableRegisters() {
      const registersData = this.$store.state.audience.importContacts.importedData?.items

      if (!registersData) return []

      return registersData.map((register) => {
        return {
          ...register,
          lines: register.lines.join(', '),
        }
      })
    },
    selectTypesOptions() {
      const importedData = this.$store.state.audience.importContacts.importedData

      if (!importedData) return []

      const optionsCurrentSelected = importedData.items.filter((item) => item.type !== null).map((element) => element.type.value) || []
      const options = [...this.selectTypes, ...(this.defaultSelectTypes ? [this.defaultSelectTypes] : [])]
      options.forEach((item) => {
        // * Reordena DATEADDED para última opção do grupo CONTACT_FIELDS
        if (item.value === 'CONTACT_FIELDS') {
          const dateAddedIndex = item.types.findIndex((type) => type.personalizationTag === 'DATEADDED')

          if (dateAddedIndex !== -1) {
            const [dateAdded] = item.types.splice(dateAddedIndex, 1)
            item.types.push(dateAdded)
          }
        }

        item.types.forEach((element) => {
          element.$isDisabled = optionsCurrentSelected.includes(element.value)
        })
      })

      return options
    },
    qndFieldsCustom() {
      if (this.selectTypesOptions.length === 0) return
      return this.selectTypesOptions.find((item) => item.value == 'CUSTOM').types.length + 1
    },
    optionMainColumnSelected() {
      const importedData = this.$store.state.audience.importContacts.importedData
      if (importedData.audienceKeys) {
        return importedData.audienceKeys.map((audienceKey) => {
          return {
            key: audienceKey.fieldName,
            value: audienceKey.value.toUpperCase(),
          }
        })
      }

      return importedData.items
        .filter((item) => item.isKey === true)
        .map((item) => {
          return {
            fileColumnPosition: item.fileColumnPosition,
            key: item.type.value,
            value: item.type.value.toUpperCase(),
          }
        })
    },
    isMatchIncomplete() {
      if (!this.$store.state.audience.importContacts.importedData?.items) return true

      const hasAnyItemUnmapped = this.$store.state.audience.importContacts.importedData.items.some((item) => {
        return item.state && item.state.key === 1
      })

      return hasAnyItemUnmapped
    },
    hasAnySMSorEmailColumn() {
      if (!this.$store.state.audience.importContacts.importedData?.items) return false

      const teste = this.$store.state.audience.importContacts.importedData.items.some((item) => {
        return item.type && (item.type.dataType.key === 6 /* Email */ || item.type.dataType.key === 7) /* SMS */
      })

      return teste
    },
    isReadyToBeValidStep() {
      return !this.isMatchIncomplete && this.hasAnySMSorEmailColumn && (this.hasAnyAudienceKeyOnImport || this.hasAnyAudienceKeyOnItem)
    },
    typeAlreadyExists() {
      if (!this.selectTypesOptions.length === 0) return false

      const allTypes = this.selectTypesOptions.reduce((acc, cur) => {
        return acc.concat(cur.types)
      }, [])

      return allTypes.some(
        (item) =>
          item.value.trim().toUpperCase() == this.newType.value.trim().toUpperCase() &&
          item.dataType.value.trim().toUpperCase() == this.newType.dataType.value.trim().toUpperCase(),
      )
    },
    isLoadingAnySelectType() {
      return this.isLoadingSelectTypes || this.isLoadingDefaultSelectTypes
    },
    showAlertKeyRequired() {
      return !this.isLoadingAnySelectType && !this.hasAnyAudienceKeyOnItem
    },
  },
  methods: {
    getSingleLabelValue(item) {
      if (!item?.value || !item?.dataType.value) return

      return `${item.value} (${item.dataType.value})`
    },

    openKeyColumnModal() {
      this.$refs.keyColumnModal.openModal()
    },

    async getDefaultFieldDataTypes() {
      try {
        this.isLoadingDefaultSelectTypes = true

        const response = await services.defaultFieldDataTypes(this.organizationId)

        if (!response.data.data) {
          throw new Error()
        }

        const types = response.data.data.map((type) => {
          return {
            dataType: type,
            group: 'new',
            value: `NEW ${type.value} FIELD`,
          }
        })

        const defaultKnow = {
          group: 'newCustom',
          value: '+ Add Custom',
          types,
        }

        this.defaultSelectTypes = defaultKnow
      } catch (error) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.message || error?.response?.data?.messages[0]?.error?.code

        if (!errorCode) {
          this.$emit('addMessage', {
            status: 'danger',
            text: this.$t(`error.codes.DEFAULT`),
          })
          return
        }

        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`general.errors.${errorCode}`),
        })
      } finally {
        this.isLoadingDefaultSelectTypes = false
      }
    },

    async getSelectTypes() {
      try {
        this.isLoadingSelectTypes = true

        const response = await services.fieldtypes(this.organizationId)

        const groupsClient = response.data.data.groups

        groupsClient[0].categories.forEach((el) => {
          el.types.forEach((item) => {
            item.group = 'DEFAULT'
          })
        })

        if (groupsClient[1]) {
          groupsClient[1].value = 'CUSTOM'
          groupsClient[1].types.forEach((el) => {
            el.group = 'CUSTOM'
          })
        }

        this.selectTypes = [...groupsClient[0].categories, groupsClient[1]]
      } catch (error) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.message || error?.response?.data?.messages[0]?.error?.code

        if (!errorCode) {
          this.$emit('addMessage', {
            status: 'danger',
            text: this.$t(`error.codes.DEFAULT`),
          })
          return
        }

        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`general.errors.${errorCode}`),
        })
      } finally {
        this.isLoadingSelectTypes = false
      }
    },

    setAttr(props) {
      if (props.option && props.option.dataType) {
        return props.option.group + '-' + props.option.dataType.key
      }

      return ''
    },

    hasPredefinedDateFormatForced(row) {
      return this.isDate && row.forcePredefinedDataFormat && row.predefinedFormat
    },

    confirmDateFormat(row) {
      if (this.selectedDataTypeAux) {
        this.$store.dispatch('audience/updateImportedDataItem', {
          type: this.selectedDataTypeAux,
          new: false,
          position: row.fileColumnPosition,
          format: this.dateFormat,
          state: { key: 2, value: 'MAPPED' },
        })
        this.clear(row)
      }
    },

    clear(row, dispatch = false) {
      if (dispatch) {
        this.$store.dispatch('audience/updateImportedDataItem', {
          type: null,
          position: row.fileColumnPosition,
          format: this.dateFormat,
          state: { key: 1, value: 'UNMAPPED' },
        })
      }

      this.newType = {
        group: '',
        value: '',
        hasName: false,
        format: null,
      }

      this.dateFormat = null
      this.selectedDataTypeAux = null
      this.selectedRowAux = null
      this.isDate = false
    },

    handleIsColumnKey(position, obj) {
      const keyValue = obj.value
      const importedData = this.$store.state.audience.importContacts.importedData

      if (importedData.audienceKeys?.some((item) => item.value.toUpperCase() === keyValue.toUpperCase())) {
        this.$store.dispatch('audience/setColumnAsKey', {
          position,
          isKey: true,
        })
        return
      }

      this.$store.dispatch('audience/setColumnAsKey', {
        position,
        isKey: false,
      })
    },

    selectDataType(option, row) {
      this.handleIsColumnKey(row.fileColumnPosition, option)

      const isOptionDate = option.dataType.value === 'DATETIME' || option.dataType.value === 'DATE'

      if (isOptionDate) {
        this.isDate = true
        this.selectedDataTypeAux = option
        this.selectedRowAux = row
      } else {
        this.isDate = false
        this.selectedDataTypeAux = null
        this.selectedRowAux = row
      }

      this.dateFormat = this.hasPredefinedDateFormatForced(row) ? row.predefinedFormat : null

      if (option.group == 'new') {
        option.hasName = false
        this.newType.key = this.qndFieldsCustom
        this.newType.group = 'CUSTOM'
        this.newType.dataType = {
          key: option.dataType.key,
          value: option.dataType.value,
        }
      }

      if (isOptionDate) return

      this.$store.dispatch('audience/updateImportedDataItem', {
        type: option,
        position: row.fileColumnPosition,
        format: this.dateFormat,
        state: { key: 2, value: 'MAPPED' },
      })
    },

    ignoreColumn(ignored, row) {
      if (ignored) {
        this.$store.dispatch('audience/updateImportedDataItem', {
          type: null,
          state: { key: 3, value: 'IGNORED' },
          position: row.fileColumnPosition,
        })
        return
      }

      const newState = row.type ? { key: 2, value: 'MAPPED' } : { key: 1, value: 'UNMAPPED' }
      this.$store.dispatch('audience/updateImportedDataItem', {
        state: newState,
        position: row.fileColumnPosition,
      })
    },

    isSelectedRowDateOrNew(item) {
      return this.selectedRowAux?.fileColumnName === item.fileColumnName && (this.isNewType || this.isDate)
    },

    setNewType(row) {
      if (this.isDate) {
        this.newType.format = this.dateFormat
      }

      if (this.typeAlreadyExists) {
        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`audience-new.match-columns.errors.name-type-already-exists`),
        })
        return
      }

      this.newType.hasName = true

      this.$store.dispatch('audience/updateImportedDataItem', {
        type: this.newType,
        new: true,
        position: row.fileColumnPosition,
        format: this.dateFormat,
        state: { key: 2, value: 'MAPPED' },
      })
      this.clear()
    },
  },
  watch: {
    isReadyToBeValidStep: {
      handler(isValid) {
        if (!isValid) {
          const newValue = this.$store.state.audience.importContacts.validSteps.filter(
            (step) => step !== this.$store.state.audience.importContacts.currentStep,
          )
          return this.$store.dispatch('audience/setImportContactsValidSteps', newValue)
        }

        this.$store.dispatch('audience/setImportContactsValidSteps', [
          ...this.$store.state.audience.importContacts.validSteps,
          this.$store.state.audience.importContacts.currentStep,
        ])
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.max-w-790 {
  max-width: 790px;
}
.max-w-890 {
  max-width: 890px;
}
.max-h-500 {
  max-height: 500px;
}
.w-450 {
  width: 450px;
}
.max-w-450 {
  max-width: 450px;
}
.max-w-150 {
  max-width: 150px;
}
.overflow-auto {
  overflow: auto;
}
.text-uppercase {
  text-transform: uppercase;
}
.hidden-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vertical-middle {
  vertical-align: middle;
}
</style>

<style lang="scss">
.match-columns {
  .senditTable tr {
    td {
      transition: opacity 0.3s ease;
    }

    &:has(td.row-ignored) {
      td {
        opacity: 0.5;
        transition: opacity 0.3s ease;

        &.row-ignored {
          opacity: 1;
        }
      }
    }
  }

  .placeholder-transform-none::placeholder {
    text-transform: none;
  }
  .multiselect {
    border-left: 2px solid #f5f5f5;

    .multiselect__content-wrapper {
      position: sticky;
    }

    &__option {
      color: var(--gray-700);
    }

    &__single {
      text-transform: uppercase;
    }

    &__optionLabelGroup {
      color: var(--gray-500);
      border-bottom: 1px solid var(--gray-500);
      display: block;
      margin: 10px 0;
      padding: 10px 0;
    }

    &__option--group {
      height: auto;
      background: none;
      padding: 10px;
      margin: 0;
      border-bottom: 1px solid var(--gray-500);
      text-transform: uppercase;
    }

    &__option--disabled {
      border: none;
      color: var(--gray-500);

      &:not(:has(.multiselect__optionLabelGroup)) {
        background-color: var(--gray-200);
      }
    }

    &__content-wrapper {
      padding: 1em;
    }

    &--disabled {
      .multiselect__select {
        display: none !important;
      }
    }
  }

  .checkbox-area .custom-control-label::before {
    top: 0.13rem;
  }
  .checkbox-area .custom-control-label::after {
    top: 0.13rem;
  }
}
</style>
